import { action, observable, computed } from 'mobx'
import { task } from 'mobx-task'
import { validationContext } from 'validx'
import { extractMessageFromError } from 'utils/errorUtil'
import { TaxRefundDeliveryMethodType } from 'src/client/domain/tax-refund-method/TaxRefundMethodTypes'
import { validateObject } from 'src/client/utils/validx-validators'
import { DirectDepositFormViewModel } from '../DeliveryMethodForms/DirectDepositForm/DirectDepositForm'
import { CheckFormViewModel } from '../DeliveryMethodForms/CheckForm/CheckForm'
import { FutureTaxCreditFormViewModel } from '../DeliveryMethodForms/FutureTaxCreditForm/FutureTaxCreditForm'

export class BusinessRefundMethodSupportingItemInputViewModel {
  /**
   * The underlying item.
   *
   * @type {RequestedSupportingItem}
   */
  @observable item

  /**
   * Whether the dialog form is showing.
   */
  @observable showingDialog = false

  @observable deliveryMethodInput = new DirectDepositFormViewModel()

  validation = validationContext(this, {
    deliveryMethodInput: [validateObject('deliveryMethodInput')],
  })

  constructor({
    item,
    flashMessageStore,
    taxRefundMethodStore,
    infoGatheringStore,
    getCurrentJob,
  }) {
    this.item = item
    this.flashMessageStore = flashMessageStore
    this.taxRefundMethodStore = taxRefundMethodStore
    this.infoGatheringStore = infoGatheringStore
    this.getCurrentJob = getCurrentJob
  }

  activate = task.resolved(async () => {
    await this.fetchBusinessTaxRefundMethod()
  })

  @computed
  get deliveryMethodTypeInput() {
    switch (true) {
      case this.deliveryMethodInput instanceof DirectDepositFormViewModel:
        return TaxRefundDeliveryMethodType.DirectDeposit
      case this.deliveryMethodInput instanceof CheckFormViewModel:
        return TaxRefundDeliveryMethodType.Check
      case this.deliveryMethodInput instanceof FutureTaxCreditFormViewModel:
        return TaxRefundDeliveryMethodType.FutureTaxCredit
      default:
        return TaxRefundDeliveryMethodType.DirectDeposit
    }
  }

  @computed
  get infoLines() {
    switch (this.taxRefundMethod.deliveryMethod.type) {
      case TaxRefundDeliveryMethodType.DirectDeposit:
        return {
          title: 'Direct deposit',
          description:
            'Provide your business bank account information upfront. The tax preparer will share your account details with the IRS, to pay out refunds automatically.',
          contactInfo: [],
        }
      case TaxRefundDeliveryMethodType.Check:
        return {
          title: 'Paper check by mail',
          description:
            'Receive a refund as a paper check. This option takes longer to process.',
          contactInfo: this.toContactInfo(
            this.taxRefundMethod.deliveryMethod.check.mailingAddress
          ),
        }
      case TaxRefundDeliveryMethodType.FutureTaxCredit:
        return {
          title: 'Applying refund to future taxes',
          description:
            'Apply your refund towards future estimated tax payments rather than receiving it as cash. Useful if you expect to owe taxes in the next quarter or year.',
          contactInfo: [],
        }
      default:
        return {
          title: '',
          description: '',
          contactInfo: [],
        }
    }
  }

  toContactInfo(address) {
    if (!address) {
      return ['Using same address as in business information.']
    }

    return [
      address.addressLine1,
      address.addressLine2,
      cityStateZip(address),
      address.country,
    ].filter(Boolean)

    function cityStateZip(address) {
      return [address.city, address.region, address.postalCode]
        .filter((line) => Boolean(line))
        .join(', ')
    }
  }

  @action.bound
  selectDeliveryMethod(deliveryMethodType) {
    switch (deliveryMethodType) {
      case TaxRefundDeliveryMethodType.DirectDeposit:
        this.deliveryMethodInput = new DirectDepositFormViewModel()
        break
      case TaxRefundDeliveryMethodType.Check:
        this.deliveryMethodInput = new CheckFormViewModel()
        break
      case TaxRefundDeliveryMethodType.FutureTaxCredit:
        this.deliveryMethodInput = new FutureTaxCreditFormViewModel()
        break
      default:
        this.deliveryMethodInput = new DirectDepositFormViewModel()
        break
    }
  }

  /**
   * Assigns the fetched profile to the view model.
   */
  @action.bound
  fillRefundMethod() {
    if (!this.taxRefundMethod) {
      return
    }

    switch (this.taxRefundMethod.deliveryMethod.type) {
      case TaxRefundDeliveryMethodType.DirectDeposit:
        this.deliveryMethodInput = new DirectDepositFormViewModel()
        this.deliveryMethodInput.parse(
          this.taxRefundMethod.deliveryMethod.directDeposit.bankAccount
        )
        break
      case TaxRefundDeliveryMethodType.FutureTaxCredit:
        this.deliveryMethodInput = new FutureTaxCreditFormViewModel()
        break
      case TaxRefundDeliveryMethodType.Check:
        this.deliveryMethodInput = new CheckFormViewModel()
        this.deliveryMethodInput.parse(
          this.taxRefundMethod.deliveryMethod.check.mailingAddress
        )
        break
    }
  }

  /**
   * The current job.
   */
  @computed
  get currentJob() {
    return this.getCurrentJob()
  }

  /**
   * The current tax refund method for the job if any
   */
  @computed
  get taxRefundMethod() {
    return this.taxRefundMethodStore.getBusinessTaxReturnMethodVersion(
      this.item.input.refundMethodId,
      this.item.input.refundMethodVersion
    )
  }

  /**
   * Fetches the business refund methpd for the job.
   */
  @task
  async fetchBusinessTaxRefundMethod() {
    if (!this.item.input?.refundMethodId) {
      return
    }

    await this.taxRefundMethodStore
      .fetchBusinessTaxRefundMethodForJob(this.currentJob.id)
      .then(() => this.fillRefundMethod())
      .catch((e) => {
        this.flashMessageStore.create({
          message: extractMessageFromError(e),
          type: 'error',
        })

        throw e
      })
  }

  /**
   * Shows the business information dialog.
   */
  @action
  showDialog() {
    this.showingDialog = true
  }

  /**
   * Closes the business information dialog.
   */
  @action
  closeDialog() {
    this.showingDialog = false
  }

  /**
   * Submits the business information.
   *
   * @returns
   */
  @task.resolved
  async submit() {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    try {
      const result =
        await this.taxRefundMethodStore.createBusinessTaxRefundMethod(
          this.currentJob.workspaceId,
          this.deliveryMethodInput.toDto()
        )

      await this.infoGatheringStore.provideBusinessRefundMethod(
        result.id,
        result.version,
        this.currentJob.id,
        this.item
      )
      this.closeDialog()
    } catch (ex) {
      this.flashMessageStore.create({
        type: 'error',
        message: extractMessageFromError(ex),
      })
    }
  }
}
